import { BrokerId } from '../config/brokerImportGuides';
import { Transaction } from '../types';

const cleanText = (value: unknown): string => String(value ?? '').replace(/\u00A0/g, ' ').trim();
const sanitizeCurrency = (value: unknown, fallback = 'GBP') => cleanText(value).toUpperCase() || fallback;

const parseLine = (text: string, delimiter = ',') => {
    const result: string[] = [];
    let current = '';
    let inQuote = false;
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (char === '"') inQuote = !inQuote;
        else if (char === delimiter && !inQuote) {
            result.push(current.trim());
            current = '';
        } else current += char;
    }
    result.push(current.trim());
    return result.map((val) => cleanText(val.replace(/^"|"$/g, '')));
};

const detectDelimiter = (lines: string[]) => {
    for (const d of [',', '\t', ';']) {
        if (parseLine(lines[0], d).length > 1) return d;
    }
    return ',';
};

const cleanHeader = (value: string) => value.trim().toLowerCase();

const findHeader = (headers: string[], candidates: string[]): number => {
    for (const candidate of candidates) {
        const idx = headers.findIndex((h) => cleanHeader(h) === cleanHeader(candidate));
        if (idx >= 0) return idx;
    }
    return -1;
};

const parseNum = (value: string): number | null => {
    const normalized = cleanText(value).replace(/,/g, '').replace(/[£$€]/g, '');
    if (!normalized) return null;
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
};

const parseDate = (raw: string): string | null => {
    const cleaned = cleanText(raw);
    if (!cleaned) return null;
    const iso = new Date(cleaned.replace(' ', 'T'));
    if (!Number.isNaN(iso.getTime())) return iso.toISOString();
    const [datePart, timePart] = cleaned.replace(/[-.]/g, '/').split(' ');
    const [a, b, c] = datePart.split('/');
    if (!a || !b || !c) return null;
    const isYearFirst = a.length === 4;
    const day = isYearFirst ? c : a;
    const month = b;
    const year = isYearFirst ? a : c;
    const normalized = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${timePart || '00:00:00'}`);
    return Number.isNaN(normalized.getTime()) ? null : normalized.toISOString();
};

const normalizeAction = (raw: string): Transaction['action'] | null => {
    const value = cleanText(raw).toLowerCase();
    if (value.includes('buy')) return 'Buy';
    if (value.includes('sell')) return 'Sell';
    if (value.includes('dividend') || value.includes('interest')) return 'Dividend';
    if (value.includes('deposit') || value.includes('transfer in')) return 'Deposit';
    if (value.includes('withdraw')) return 'Withdrawal';
    return null;
};

const STRICT_DUAL_HEADERS = {
    priceCurrency: ['currency (price)', 'currency (price / share)'],
    totalCurrency: ['currency (total)'],
    fxRate: ['exchange rate'],
};

export const parseGenericCsv = (csvText: string, brokerType: BrokerId): Transaction[] => {
    const lines = csvText.split(/\r?\n/).filter((line) => cleanText(line).length > 0);
    if (lines.length < 2) return [];

    const delimiter = detectDelimiter(lines);
    const headers = parseLine(lines[0], delimiter);
    const normalizedHeaders = headers.map(cleanHeader);

    const idxDate = findHeader(normalizedHeaders, ['date', 'time', 'timestamp', 'executed at']);
    const idxAction = findHeader(normalizedHeaders, ['action', 'type', 'side', 'transaction type']);
    const idxSymbol = findHeader(normalizedHeaders, ['symbol', 'ticker', 'instrument']);
    const idxName = findHeader(normalizedHeaders, ['name', 'instrument name', 'description']);
    const idxIsin = findHeader(normalizedHeaders, ['isin']);
    const idxQty = findHeader(normalizedHeaders, ['quantity', 'shares', 'no. of shares', 'units']);
    const idxPrice = findHeader(normalizedHeaders, ['price / share', 'price per share', 'price']);
    const idxTotal = findHeader(normalizedHeaders, ['total', 'amount', 'gross amount', 'net amount', 'result']);
    const idxId = findHeader(normalizedHeaders, ['id', 'transaction id', 'order id', 'broker transaction id']);

    const idxPriceCurrency = findHeader(normalizedHeaders, STRICT_DUAL_HEADERS.priceCurrency);
    const idxTotalCurrency = findHeader(normalizedHeaders, STRICT_DUAL_HEADERS.totalCurrency);
    const idxFxRate = findHeader(normalizedHeaders, STRICT_DUAL_HEADERS.fxRate);
    const idxCurrency = findHeader(normalizedHeaders, ['currency']);

    const hasStrictDualCurrency = idxPriceCurrency >= 0 && idxTotalCurrency >= 0 && idxFxRate >= 0;
    const ambiguousCurrency = !hasStrictDualCurrency && idxCurrency >= 0 && (idxPrice >= 0 || idxTotal >= 0);

    const parsed: Transaction[] = [];
    for (let i = 1; i < lines.length; i++) {
        const cols = parseLine(lines[i], delimiter);
        const date = parseDate(idxDate >= 0 ? cols[idxDate] : '');
        const action = normalizeAction(idxAction >= 0 ? cols[idxAction] : '');
        if (!date || !action) continue;

        const quantity = Math.abs(parseNum(idxQty >= 0 ? cols[idxQty] : '') ?? 0);
        const directPrice = parseNum(idxPrice >= 0 ? cols[idxPrice] : '') ?? undefined;
        const total = parseNum(idxTotal >= 0 ? cols[idxTotal] : '') ?? undefined;

        const totalCurrency = sanitizeCurrency(idxTotalCurrency >= 0 ? cols[idxTotalCurrency] : idxCurrency >= 0 ? cols[idxCurrency] : 'GBP', 'GBP');
        const priceCurrency = sanitizeCurrency(idxPriceCurrency >= 0 ? cols[idxPriceCurrency] : totalCurrency, totalCurrency);
        const fxRate = parseNum(idxFxRate >= 0 ? cols[idxFxRate] : '') ?? undefined;

        const computedPrice =
            directPrice && directPrice > 0
                ? directPrice
                : quantity > 0 && total && total > 0
                  ? total / quantity
                  : 0;

        const brokerTxId = cleanText(idxId >= 0 ? cols[idxId] : '') || undefined;
        const symbol = cleanText(idxSymbol >= 0 ? cols[idxSymbol] : '') || 'CASH';

        parsed.push({
            id: brokerTxId || `GEN_${brokerType}_${i}_${symbol}`.replace(/[^a-zA-Z0-9_]/g, ''),
            brokerTxId,
            date,
            action,
            symbol: symbol.toUpperCase(),
            displaySymbol: symbol.toUpperCase(),
            isin: cleanText(idxIsin >= 0 ? cols[idxIsin] : '') || undefined,
            name: cleanText(idxName >= 0 ? cols[idxName] : '') || symbol.toUpperCase(),
            quantity,
            shares: quantity || undefined,
            price: computedPrice,
            pricePerShare: directPrice,
            priceCurrency,
            amount: Math.abs(total ?? quantity * computedPrice),
            total,
            currency: totalCurrency,
            totalCurrency,
            fxRate: hasStrictDualCurrency ? fxRate : undefined,
            source: 'csv',
            provider: brokerType,
            sourceLabel: `${brokerType} CSV`,
            reviewRequired: ambiguousCurrency || (!hasStrictDualCurrency && idxPriceCurrency >= 0),
            unresolved: ambiguousCurrency || undefined,
        });
    }

    return parsed;
};
